// Phase 9.7.3 — Vendor↔Item Links admin section for the Ordering Rules page.
// Small self-contained widget: upload xlsx, list current links (filterable
// by vendor + keyword), add a single manual link. Reuses shadcn primitives.
//
// Rendered by `FnbPoOrderingRules.jsx` below the "Manage unconfigured
// items" banner.

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Link2, Upload } from "lucide-react";

const SOURCE_META = {
  file_upload: { label: "File", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  manual: { label: "Manual", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  ordering_config: { label: "Config", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  vendor_rates: { label: "Rates", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function VendorLinksSection() {
  const [rows, setRows] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [addSku, setAddSku] = useState("");
  const [addVendor, setAddVendor] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "1000");
      if (vendorFilter) params.set("vendor_name", vendorFilter);
      if (search) params.set("search", search);
      const res = await api.get(`/fnb/admin/vendor-links?${params}`);
      setRows(res.data.links || []);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [vendorFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Group by SKU so we can show comma-joined vendors + primary flag.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!m.has(r.item_sku)) m.set(r.item_sku, []);
      m.get(r.item_sku).push(r);
    }
    // Emit sorted by SKU asc, each row with primary vendor first.
    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([sku, links]) => ({
        sku,
        links: [...links].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)),
      }));
  }, [rows]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(
        "/fnb/admin/upload-vendor-links",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploadResult(res.data);
      toast.success(
        `Parsed ${res.data.parsed_rows} rows — ${res.data.upserted} link${
          res.data.upserted === 1 ? "" : "s"
        } upserted`,
      );
      load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setUploading(false);
    }
  };

  const doAdd = async () => {
    if (!addSku.trim() || !addVendor.trim()) {
      toast.error("SKU and Vendor are required");
      return;
    }
    setAdding(true);
    try {
      await api.post("/fnb/admin/vendor-links", {
        item_sku: addSku.trim(),
        vendor_name: addVendor.trim(),
        is_primary: false,
      });
      toast.success(`Linked ${addSku.trim()} to ${addVendor.trim()} (manual-locked)`);
      setAddSku(""); setAddVendor("");
      load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setAdding(false);
    }
  };

  const doDelete = async (sku, vendorName) => {
    try {
      await api.delete(
        `/fnb/admin/vendor-links/${encodeURIComponent(sku)}/${encodeURIComponent(vendorName)}`,
      );
      toast.success(`Removed ${sku} → ${vendorName}`);
      load();
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  return (
    <Card data-testid="vendor-links-section">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Vendor ↔ Item Links
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">
              Which vendor(s) supply each SKU. Drives the vendor-scoped
              autocomplete on Manual PO. Upload the &ldquo;Vendor wise
              items&rdquo; xlsx to bulk-refresh; use inline form for
              one-offs.
            </p>
          </div>
          <label
            className={
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer " +
              (uploading
                ? "opacity-60 cursor-not-allowed border-border/60"
                : "border-border/70 hover:bg-background")
            }
            data-testid="vendor-links-upload-label"
          >
            <Upload className="w-3.5 h-3.5" />
            {uploading ? "Uploading…" : "Upload Vendor Links"}
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) doUpload(f);
              }}
              data-testid="vendor-links-upload-input"
            />
          </label>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {uploadResult && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
            data-testid="vendor-links-upload-result"
          >
            Parsed <b>{uploadResult.parsed_rows}</b> · Auto-matched{" "}
            <b>{uploadResult.auto_matched}</b> · Upserted{" "}
            <b>{uploadResult.upserted}</b> · Preserved manual{" "}
            <b>{uploadResult.preserved_manual}</b>
            {uploadResult.needs_review > 0 && (
              <> · <b>{uploadResult.needs_review}</b> row(s) need review</>
            )}
            {uploadResult.unresolved_vendors?.length > 0 && (
              <> · Unresolved: <b>{uploadResult.unresolved_vendors.join(", ")}</b></>
            )}
          </div>
        )}

        {/* Filters + manual add */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            placeholder="Filter by vendor…"
            className="h-8 text-xs max-w-[180px]"
            data-testid="vendor-links-vendor-filter"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU/vendor…"
            className="h-8 text-xs max-w-[180px]"
            data-testid="vendor-links-search"
          />
          <div className="flex-1" />
          <div className="flex items-center gap-1.5">
            <Input
              value={addSku}
              onChange={(e) => setAddSku(e.target.value)}
              placeholder="SKU"
              className="h-8 text-xs w-24"
              data-testid="vendor-links-add-sku"
            />
            <Input
              value={addVendor}
              onChange={(e) => setAddVendor(e.target.value)}
              placeholder="Vendor name"
              className="h-8 text-xs w-40"
              data-testid="vendor-links-add-vendor"
            />
            <Button
              size="sm"
              onClick={doAdd}
              disabled={adding}
              data-testid="vendor-links-add-btn"
            >
              {adding ? "…" : "Add link"}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border border-border/70 overflow-auto max-h-96">
          <Table data-testid="vendor-links-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">SKU</TableHead>
                <TableHead>Vendor(s)</TableHead>
                <TableHead className="w-16 text-center">Primary</TableHead>
                <TableHead className="w-20 text-center">Source</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-4">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && grouped.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-6">
                    No links match. Upload a Vendor wise items xlsx or add a
                    manual link above.
                  </TableCell>
                </TableRow>
              )}
              {grouped.map(({ sku, links }) => (
                <TableRow key={sku} data-testid={`vendor-links-row-${sku}`}>
                  <TableCell className="font-mono text-xs">{sku}</TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {links.map((l) => {
                        const meta = SOURCE_META[l.source] || {
                          label: l.source, cls: "bg-slate-100 text-slate-700 border-slate-200",
                        };
                        return (
                          <span
                            key={l.vendor_name}
                            className="inline-flex items-center gap-1 rounded-md border border-border/60 px-1.5 py-0.5"
                            title={`Source: ${l.source} • ${l.is_primary ? "primary" : "secondary"}`}
                            data-testid={`vendor-link-chip-${sku}-${l.vendor_name}`}
                          >
                            <span>{l.vendor_name}</span>
                            {l.is_primary && (
                              <Badge variant="outline" className="text-[9px] px-1 border-emerald-300 text-emerald-800">
                                ★
                              </Badge>
                            )}
                            <Badge variant="outline" className={`text-[9px] px-1 ${meta.cls}`}>
                              {meta.label}
                            </Badge>
                            <button
                              type="button"
                              onClick={() => doDelete(sku, l.vendor_name)}
                              className="text-muted-foreground hover:text-destructive text-[11px] leading-none"
                              aria-label={`Remove ${sku} → ${l.vendor_name}`}
                              data-testid={`vendor-link-remove-${sku}-${l.vendor_name}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-center">
                    {links.find((l) => l.is_primary)?.vendor_name || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-center">
                    <Badge variant="outline" className="text-[10px]">
                      {links.length === 1
                        ? SOURCE_META[links[0].source]?.label || links[0].source
                        : `${links.length} sources`}
                    </Badge>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Showing {grouped.length} SKU{grouped.length === 1 ? "" : "s"} · {rows.length} link{rows.length === 1 ? "" : "s"}
          {vendorFilter ? ` matching vendor "${vendorFilter}"` : ""}
        </div>
      </CardContent>
    </Card>
  );
}
